import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import AccessDeniedModal from './AccessDeniedModal';
import { useLanguage } from '../LanguageContext';

const getTabs = (t) => [
    { key: 'suppliers', label: t.suppliers, addLabel: t.add_supplier },
    { key: 'packaging', label: t.packaging, addLabel: t.add_material },
    { key: 'customers', label: t.customers, addLabel: t.add_customer },
    { key: 'products', label: t.products, addLabel: t.add_product },
    { key: 'raw', label: t.raw_materials, addLabel: t.add_material },
    { key: 'regions', label: t.regions, addLabel: t.add_region }
];



const Management = ({ user }) => {
    const { t, isRtl } = useLanguage();
    const TABS = getTabs(t);
    const [activeTab, setActiveTab] = useState('suppliers');
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [showUnauthorized, setShowUnauthorized] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, type: null, name: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 720 : false);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 720);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // ---- Suppliers state ----
    const [suppliers, setSuppliers] = useState([]);
    const [suppLoading, setSuppLoading] = useState(false);
    const [suppModal, setSuppModal] = useState(false);
    const [suppEditing, setSuppEditing] = useState(false);
    const [suppEditId, setSuppEditId] = useState(null);
    const [suppForm, setSuppForm] = useState({ supplier_name: '', supplier_code: '' });

    // ---- Packaging state ----
    const [packaging, setPackaging] = useState([]);
    const [packLoading, setPackLoading] = useState(false);
    const [packModal, setPackModal] = useState(false);
    const [packEditing, setPackEditing] = useState(false);
    const [packEditId, setPackEditId] = useState(null);
    const [packForm, setPackForm] = useState({ name: '', code: '', minimumQuantityRequired: '', weightHandler: '' });

    // ---- Customers state ----
    const [customers, setCustomers] = useState([]);
    const [custLoading, setCustLoading] = useState(false);
    const [custModal, setCustModal] = useState(false);
    const [custEditing, setCustEditing] = useState(false);
    const [custEditId, setCustEditId] = useState(null);
    const [custForm, setCustForm] = useState({ customer_name: '', code: '' });

    // ---- Approved Products state ----
    const [approvedProducts, setApprovedProducts] = useState([]);
    const [prodLoading, setProdLoading] = useState(false);
    const [prodModal, setProdModal] = useState(false);
    const [prodEditing, setProdEditing] = useState(false);
    const [prodEditId, setProdEditId] = useState(null);
    const [prodForm, setProdForm] = useState({ name: '', code: '' });

    // ---- Approved Raw Materials state ----
    const [approvedRaw, setApprovedRaw] = useState([]);
    const [rawLoading, setRawLoading] = useState(false);
    const [rawModal, setRawModal] = useState(false);
    const [rawEditing, setRawEditing] = useState(false);
    const [rawEditId, setRawEditId] = useState(null);
    const [rawForm, setRawForm] = useState({ name: '', code: '', minQuantityThreshold: '' });

    // ---- Regions state ----
    const [regions, setRegions] = useState([]);
    const [regLoading, setRegLoading] = useState(false);
    const [regModal, setRegModal] = useState(false);
    const [regEditing, setRegEditing] = useState(false);
    const [regEditId, setRegEditId] = useState(null);
    const [regForm, setRegForm] = useState({ name: '' });

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // ---- Click outside to close menu ----
    useEffect(() => {
        const onDocClick = (e) => {
            if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) setMenuOpenId(null);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    // ---- Fetch functions ----
    const fetchSuppliers = async () => {
        setSuppLoading(true);
        try { const res = await axios.get(`${API_BASE}/suppliers`, { headers: getAuthHeaders() }); setSuppliers(res.data || []); }
        catch (err) { console.error(err); }
        finally { setSuppLoading(false); }
    };
    const fetchPackaging = async () => {
        setPackLoading(true);
        try { const res = await axios.get(`${API_BASE}/packagingmaterials`, { headers: getAuthHeaders() }); setPackaging(res.data || []); }
        catch (err) { console.error(err); }
        finally { setPackLoading(false); }
    };
    const fetchCustomers = async () => {
        setCustLoading(true);
        try { const res = await axios.get(`${API_BASE}/customers`, { headers: getAuthHeaders() }); setCustomers(res.data || []); }
        catch (err) { console.error(err); }
        finally { setCustLoading(false); }
    };
    const fetchApprovedProducts = async () => {
        setProdLoading(true);
        try { const res = await axios.get(`${API_BASE}/approved-products`, { headers: getAuthHeaders() }); setApprovedProducts(res.data || []); }
        catch (err) { console.error(err); }
        finally { setProdLoading(false); }
    };
    const fetchApprovedRaw = async () => {
        setRawLoading(true);
        try { const res = await axios.get(`${API_BASE}/approved-raw-materials`, { headers: getAuthHeaders() }); setApprovedRaw(res.data || []); }
        catch (err) { console.error(err); }
        finally { setRawLoading(false); }
    };
    const fetchRegions = async () => {
        setRegLoading(true);
        try { const res = await axios.get(`${API_BASE}/regions`, { headers: getAuthHeaders() }); setRegions(res.data || []); }
        catch (err) { console.error(err); }
        finally { setRegLoading(false); }
    };

    useEffect(() => { fetchSuppliers(); fetchPackaging(); fetchCustomers(); fetchApprovedProducts(); fetchApprovedRaw(); fetchRegions(); }, []);

    // Clear search when switching tabs
    const handleTabChange = (key) => {
        setActiveTab(key);
        setSearchQuery('');
        setMenuOpenId(null);
    };

    // ---- Supplier CRUD ----
    const openSuppAdd = () => { setSuppEditing(false); setSuppEditId(null); setSuppForm({ supplier_name: '', supplier_code: '' }); setSuppModal(true); };
    const openSuppEdit = (it) => { setSuppEditing(true); setSuppEditId(it._id); setSuppForm({ supplier_name: it.supplier_name || '', supplier_code: it.supplier_code || '' }); setSuppModal(true); };
    const handleSuppDelete = (it) => {
        setDeleteConfirm({ open: true, id: it._id, type: 'supplier', name: it.supplier_name });
    };
    const handleSuppSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (suppEditing && suppEditId) await axios.put(`${API_BASE}/suppliers/${suppEditId}`, suppForm, { headers: getAuthHeaders() });
            else await axios.post(`${API_BASE}/suppliers`, suppForm, { headers: getAuthHeaders() });
            setSuppModal(false); fetchSuppliers();
        } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
        finally { setActionLoading(false); }
    };

    // ---- Packaging CRUD ----
    const openPackAdd = () => { setPackEditing(false); setPackEditId(null); setPackForm({ name: '', code: '', minimumQuantityRequired: '', weightHandler: '' }); setPackModal(true); };
    const openPackEdit = (it) => { setPackEditing(true); setPackEditId(it._id); setPackForm({ name: it.name || '', code: it.code || '', minimumQuantityRequired: it.minimumQuantityRequired || '', weightHandler: it.weightHandler || '' }); setPackModal(true); };
    const handlePackDelete = (it) => {
        setDeleteConfirm({ open: true, id: it._id, type: 'packaging', name: it.name });
    };
    const handlePackSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            const payload = {
                name: packForm.name,
                code: packForm.code,
                minimumQuantityRequired: packForm.minimumQuantityRequired ? Number(packForm.minimumQuantityRequired) : 0,
                weightHandler: packForm.weightHandler
            };
            if (packEditing && packEditId) await axios.put(`${API_BASE}/packagingmaterials/${packEditId}`, payload, { headers: getAuthHeaders() });
            else await axios.post(`${API_BASE}/packagingmaterials`, payload, { headers: getAuthHeaders() });
            setPackModal(false); fetchPackaging();
        } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
        finally { setActionLoading(false); }
    };

    // ---- Customer CRUD ----
    const openCustAdd = () => { setCustEditing(false); setCustEditId(null); setCustForm({ customer_name: '', code: '' }); setCustModal(true); };
    const openCustEdit = (it) => { setCustEditing(true); setCustEditId(it._id); setCustForm({ customer_name: it.customer_name || '', code: it.code || '' }); setCustModal(true); };
    const handleCustDelete = (it) => {
        setDeleteConfirm({ open: true, id: it._id, type: 'customer', name: it.customer_name });
    };
    const handleCustSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (custEditing && custEditId) await axios.put(`${API_BASE}/customers/${custEditId}`, custForm, { headers: getAuthHeaders() });
            else await axios.post(`${API_BASE}/customers`, custForm, { headers: getAuthHeaders() });
            setCustModal(false); fetchCustomers();
        } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
        finally { setActionLoading(false); }
    };

    // ---- Approved Product CRUD ----
    const openProdAdd = () => { setProdEditing(false); setProdEditId(null); setProdForm({ name: '', code: '' }); setProdModal(true); };
    const openProdEdit = (it) => { setProdEditing(true); setProdEditId(it._id); setProdForm({ name: it.name || '', code: it.code || '' }); setProdModal(true); };
    const handleProdDelete = (it) => {
        setDeleteConfirm({ open: true, id: it._id, type: 'product', name: it.name });
    };
    const handleProdSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (prodEditing && prodEditId) await axios.put(`${API_BASE}/approved-products/${prodEditId}`, prodForm, { headers: getAuthHeaders() });
            else await axios.post(`${API_BASE}/approved-products`, prodForm, { headers: getAuthHeaders() });
            setProdModal(false); fetchApprovedProducts();
        } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
        finally { setActionLoading(false); }
    };

    // ---- Approved Raw Material CRUD ----
    const openRawAdd = () => { setRawEditing(false); setRawEditId(null); setRawForm({ name: '', code: '', minQuantityThreshold: '' }); setRawModal(true); };
    const openRawEdit = (it) => { setRawEditing(true); setRawEditId(it._id); setRawForm({ name: it.name || '', code: it.code || '', minQuantityThreshold: it.minQuantityThreshold || '' }); setRawModal(true); };
    const handleRawDelete = (it) => {
        setDeleteConfirm({ open: true, id: it._id, type: 'raw_material', name: it.name });
    };
    const handleRawSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (rawEditing && rawEditId) await axios.put(`${API_BASE}/approved-raw-materials/${rawEditId}`, rawForm, { headers: getAuthHeaders() });
            else await axios.post(`${API_BASE}/approved-raw-materials`, rawForm, { headers: getAuthHeaders() });
            setRawModal(false); fetchApprovedRaw();
        } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
        finally { setActionLoading(false); }
    };

    // ---- Region CRUD ----
    const openRegAdd = () => { setRegEditing(false); setRegEditId(null); setRegForm({ name: '' }); setRegModal(true); };
    const openRegEdit = (it) => { setRegEditing(true); setRegEditId(it._id); setRegForm({ name: it.name || '' }); setRegModal(true); };
    const handleRegDelete = (it) => {
        setDeleteConfirm({ open: true, id: it._id, type: 'region', name: it.name });
    };
    const handleRegSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (regEditing && regEditId) await axios.put(`${API_BASE}/regions/${regEditId}`, regForm, { headers: getAuthHeaders() });
            else await axios.post(`${API_BASE}/regions`, regForm, { headers: getAuthHeaders() });
            setRegModal(false); fetchRegions();
        } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
        finally { setActionLoading(false); }
    };

    const executeDelete = async () => {
        const { id, type } = deleteConfirm;
        setActionLoading(true);
        try {
            let url = '';
            let callback = null;
            if (type === 'supplier') { url = `${API_BASE}/suppliers/${id}`; callback = fetchSuppliers; }
            else if (type === 'packaging') { url = `${API_BASE}/packagingmaterials/${id}`; callback = fetchPackaging; }
            else if (type === 'customer') { url = `${API_BASE}/customers/${id}`; callback = fetchCustomers; }
            else if (type === 'product') { url = `${API_BASE}/approved-products/${id}`; callback = fetchApprovedProducts; }
            else if (type === 'raw_material') { url = `${API_BASE}/approved-raw-materials/${id}`; callback = fetchApprovedRaw; }
            else if (type === 'region') { url = `${API_BASE}/regions/${id}`; callback = fetchRegions; }

            await axios.delete(url, { headers: getAuthHeaders() });
            setDeleteConfirm({ open: false, id: null, type: null, name: '' });
            if (callback) callback();
        } catch (err) {
            alert(err.response?.data?.msg || t.delete_failed);
            setDeleteConfirm({ open: false, id: null, type: null, name: '' });
        } finally {
            setActionLoading(false);
        }
    };


    // ---- Filtered data ----
    const filteredSuppliers = suppliers.filter(it => {
        const term = searchQuery.toLowerCase();
        return term === '' ||
            it.supplier_name?.toLowerCase().includes(term) ||
            it.supplier_code?.toLowerCase().includes(term);
    });
    const filteredPackaging = packaging.filter(it => {
        const term = searchQuery.toLowerCase();
        return term === '' ||
            it.name?.toLowerCase().includes(term) ||
            it.code?.toLowerCase().includes(term) ||
            it.weightHandler?.toLowerCase().includes(term) ||
            String(it.minimumQuantityRequired || '').toLowerCase().includes(term);
    });
    const filteredCustomers = customers.filter(it => {
        const term = searchQuery.toLowerCase();
        return term === '' ||
            it.customer_name?.toLowerCase().includes(term) ||
            it.code?.toLowerCase().includes(term);
    });
    const filteredProducts = approvedProducts.filter(it => {
        const term = searchQuery.toLowerCase();
        return term === '' ||
            it.name?.toLowerCase().includes(term) ||
            it.code?.toLowerCase().includes(term) ||
            it.uuid?.toLowerCase().includes(term);
    });
    const filteredRawMaterials = approvedRaw.filter(it => {
        const term = searchQuery.toLowerCase();
        return term === '' ||
            it.name?.toLowerCase().includes(term) ||
            it.code?.toLowerCase().includes(term) ||
            it.uuid?.toLowerCase().includes(term) ||
            String(it.minQuantityThreshold || '').toLowerCase().includes(term);
    });

    // ---- Which add handler for current tab ----
    const handleAdd = () => {
        if (user?.type === 'Member') {
            setShowUnauthorized(true);
            return;
        }
        if (activeTab === 'suppliers') openSuppAdd();
        else if (activeTab === 'packaging') openPackAdd();
        else if (activeTab === 'customers') openCustAdd();
        else if (activeTab === 'products') openProdAdd();
        else if (activeTab === 'regions') openRegAdd();
        else openRawAdd();
    };

    // ---- Get current list for floating menu ----
    const getCurrentList = () => {
        if (activeTab === 'suppliers') return suppliers;
        if (activeTab === 'packaging') return packaging;
        if (activeTab === 'customers') return customers;
        if (activeTab === 'products') return approvedProducts;
        if (activeTab === 'regions') return regions;
        return approvedRaw;
    };

    const getEditHandler = () => {
        if (activeTab === 'suppliers') return openSuppEdit;
        if (activeTab === 'packaging') return openPackEdit;
        if (activeTab === 'customers') return openCustEdit;
        if (activeTab === 'products') return openProdEdit;
        if (activeTab === 'regions') return openRegEdit;
        return openRawEdit;
    };

    const getDeleteHandler = () => {
        if (activeTab === 'suppliers') return handleSuppDelete;
        if (activeTab === 'packaging') return handlePackDelete;
        if (activeTab === 'customers') return handleCustDelete;
        if (activeTab === 'products') return handleProdDelete;
        if (activeTab === 'regions') return handleRegDelete;
        return handleRawDelete;
    };

    const isLoading = activeTab === 'suppliers' ? suppLoading : activeTab === 'packaging' ? packLoading : activeTab === 'customers' ? custLoading : activeTab === 'products' ? prodLoading : activeTab === 'regions' ? regLoading : rawLoading;
    const currentTabInfo = TABS.find(t => t.key === activeTab);
    const filterPlaceholder = `${t.filter} ${currentTabInfo?.label.toLowerCase()}...`;

    const ThreeDotBtn = ({ item }) => (
        <button className="menu-btn" onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
            setMenuOpenId(menuOpenId === item._id ? null : item._id);
        }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 4 }}>
            <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
        </button>
    );

    // ---- Render tables ----
    const renderSuppliersTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
            <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.supplier}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.code}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600, width: 60 }}></th>
                </tr>
            </thead>
            <tbody>
                {filteredSuppliers.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_suppliers}</td></tr>
                ) : filteredSuppliers.map(it => (
                    <tr key={it._id} style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.supplier_name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.supplier_code || '-'}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={e => e.stopPropagation()}><ThreeDotBtn item={it} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderPackagingTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
            <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.code}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.min_qty}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.weight_handler}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600, width: 60 }}></th>
                </tr>
            </thead>
            <tbody>
                {filteredPackaging.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_packing_found}</td></tr>
                ) : filteredPackaging.map(it => (
                    <tr key={it._id} style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.code || '-'}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.minimumQuantityRequired ?? 0}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.weightHandler || '-'}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={e => e.stopPropagation()}><ThreeDotBtn item={it} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderCustomersTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
            <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.customer_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.customer_code}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600, width: 60 }}></th>
                </tr>
            </thead>
            <tbody>
                {filteredCustomers.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_customers}</td></tr>
                ) : filteredCustomers.map(it => (
                    <tr key={it._id} style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.customer_name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.code || '-'}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={e => e.stopPropagation()}><ThreeDotBtn item={it} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderProductsTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
            <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.product_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.code}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600, width: 60 }}></th>
                </tr>
            </thead>
            <tbody>
                {filteredProducts.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_products_found}</td></tr>
                ) : filteredProducts.map(it => (
                    <tr key={it._id} style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.code || '-'}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={e => e.stopPropagation()}><ThreeDotBtn item={it} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderRawMaterialsTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
            <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.material_name}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.code}</th>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.min_qty}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600, width: 60 }}></th>
                </tr>
            </thead>
            <tbody>
                {filteredRawMaterials.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_raw_materials}</td></tr>
                ) : filteredRawMaterials.map(it => (
                    <tr key={it._id} style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.name}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.code || '-'}</td>
                        <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.minQuantityThreshold || 0}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={e => e.stopPropagation()}><ThreeDotBtn item={it} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderRegionsTable = () => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
            <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                    <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.region_name}</th>
                    <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600, width: 60 }}></th>
                </tr>
            </thead>
            <tbody>
                {regions.filter(it => it.name?.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 ? (
                    <tr><td colSpan={2} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_regions}</td></tr>
                ) : regions.filter(it => it.name?.toLowerCase().includes(searchQuery.toLowerCase())).map(it => (
                    <tr key={it._id} style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.name}</td>
                        <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={e => e.stopPropagation()}><ThreeDotBtn item={it} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', fontSize: 13, color: '#1a1c1d', outline: 'none', boxSizing: 'border-box' };

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
            {/* Page Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#1a1c1d' }}>{t.management}</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6d7175' }}>{t.management_subtitle}</p>
                </div>
                <button onClick={handleAdd} style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    transition: 'background 0.2s', flexDirection: isRtl ? 'row-reverse' : 'row'
                }}
                    onMouseOver={e => e.target.style.background = '#1565c0'}
                    onMouseOut={e => e.target.style.background = '#1976d2'}
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: isRtl ? 'rotate(180deg)' : 'none' }}><path d="M7 1v12M1 7h12" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
                    {currentTabInfo?.addLabel}
                </button>
            </div>

            {/* Table Card */}
            <div style={{
                background: '#fff', borderRadius: 12, border: '1px solid #ebebeb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)', overflow: 'hidden'
            }}>
                {/* Tabs */}
                <div style={{
                    display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #f1f1f1', padding: '0 16px', background: '#fff'
                }}>
                    {TABS.map(tab => (
                        <div key={tab.key} onClick={() => handleTabChange(tab.key)} style={{
                            padding: '12px 16px', fontSize: 13,
                            fontWeight: activeTab === tab.key ? 600 : 500,
                            color: activeTab === tab.key ? '#1a1c1d' : '#6d7175',
                            cursor: 'pointer',
                            borderBottom: activeTab === tab.key ? '3px solid #1976d2' : '3px solid transparent',
                            marginBottom: -1, transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                        }}>
                            {tab.label}
                        </div>
                    ))}
                </div>

                {/* Search / Filter Bar */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f1f1', display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                        <span style={{ position: 'absolute', [isRtl ? 'right' : 'left']: 10, top: '50%', transform: 'translateY(-50%)', color: '#8c9196' }}>
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M19 19L14.65 14.65M17 9C17 13.4183 13.4183 17 9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                        </span>
                        <input
                            placeholder={filterPlaceholder}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: '100%', padding: isRtl ? '8px 34px 8px 12px' : '8px 12px 8px 34px', borderRadius: 6, border: '1px solid #d2d2d2', fontSize: 13, color: '#1a1c1d', outline: 'none', boxSizing: 'border-box', textAlign: isRtl ? 'right' : 'left' }}
                        />
                    </div>
                </div>

                {/* Table Content */}
                {isLoading ? (
                    <Loading message={isRtl ? `${t.loading} ${currentTabInfo?.label}...` : `${t.loading} ${currentTabInfo?.label.toLowerCase()}...`} />
                ) : (
                    <div key={activeTab} className="fade-in" style={{ overflowX: 'auto' }}>
                        {activeTab === 'suppliers' && renderSuppliersTable()}
                        {activeTab === 'packaging' && renderPackagingTable()}
                        {activeTab === 'customers' && renderCustomersTable()}
                        {activeTab === 'products' && renderProductsTable()}
                        {activeTab === 'raw' && renderRawMaterialsTable()}
                        {activeTab === 'regions' && renderRegionsTable()}
                    </div>
                )}
            </div>

            {/* ============== MODALS ============== */}

            {/* Supplier Modal */}
            {suppModal && (
                <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
                    <div className="modal-fade-in" style={{ width: 480, maxWidth: '100%', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{suppEditing ? t.edit_supplier : t.add_supplier}</h2>
                            <button onClick={() => setSuppModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
                        </div>
                        <form onSubmit={handleSuppSubmit}>
                            <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.supplier_name}</label>
                                    <input name="supplier_name" value={suppForm.supplier_name} onChange={(e) => setSuppForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.supplier_code}</label>
                                    <input name="supplier_code" value={suppForm.supplier_code} onChange={(e) => setSuppForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <button type="button" onClick={() => setSuppModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf', background: '#fff', fontSize: 13, cursor: 'pointer' }}>{t.cancel_btn}</button>
                                <button type="submit" disabled={actionLoading} style={{
                                    padding: '8px 16px', borderRadius: 8, border: 'none',
                                    background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
                                    cursor: actionLoading ? 'not-allowed' : 'pointer', minWidth: 80,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {actionLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : (suppEditing ? t.save : t.create)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Packaging Modal */}
            {packModal && (
                <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
                    <div className="modal-fade-in" style={{ width: 600, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{packEditing ? t.edit_packaging_material || 'Edit Packaging Material' : t.add_packaging_material || 'Add Packaging Material'}</h2>
                            <button onClick={() => setPackModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
                        </div>
                        <form onSubmit={handlePackSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.name}</label>
                                    <input name="name" value={packForm.name} onChange={(e) => setPackForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.code}</label>
                                    <input name="code" value={packForm.code} onChange={(e) => setPackForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.min_qty_required}</label>
                                    <input name="minimumQuantityRequired" type="number" value={packForm.minimumQuantityRequired} onChange={(e) => setPackForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.weight_handler}</label>
                                    <input name="weightHandler" value={packForm.weightHandler} onChange={(e) => setPackForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <button type="button" onClick={() => setPackModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf', background: '#fff', fontSize: 13, cursor: 'pointer' }}>{t.cancel_btn}</button>
                                <button type="submit" disabled={actionLoading} style={{
                                    padding: '8px 16px', borderRadius: 8, border: 'none',
                                    background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
                                    cursor: actionLoading ? 'not-allowed' : 'pointer', minWidth: 80,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {actionLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : (packEditing ? t.save : t.create)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Customer Modal */}
            {custModal && (
                <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
                    <div className="modal-fade-in" style={{ width: 480, maxWidth: '100%', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{custEditing ? t.edit_customer : t.add_customer}</h2>
                            <button onClick={() => setCustModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
                        </div>
                        <form onSubmit={handleCustSubmit}>
                            <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.customer_name}</label>
                                    <input name="customer_name" value={custForm.customer_name} onChange={(e) => setCustForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.customer_code}</label>
                                    <input name="code" value={custForm.code} onChange={(e) => setCustForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <button type="button" onClick={() => setCustModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf', background: '#fff', fontSize: 13, cursor: 'pointer' }}>{t.cancel_btn}</button>
                                <button type="submit" disabled={actionLoading} style={{
                                    padding: '8px 16px', borderRadius: 8, border: 'none',
                                    background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
                                    cursor: actionLoading ? 'not-allowed' : 'pointer', minWidth: 80,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {actionLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : (custEditing ? t.save : t.create)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Approved Product Modal */}
            {prodModal && (
                <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
                    <div className="modal-fade-in" style={{ width: 480, maxWidth: '100%', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{prodEditing ? t.edit_product_definition || 'Edit Product Definition' : t.add_product_definition || 'Add Product Definition'}</h2>
                            <button onClick={() => setProdModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
                        </div>
                        <form onSubmit={handleProdSubmit}>
                            <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.product_name}</label>
                                    <input name="name" value={prodForm.name} onChange={(e) => setProdForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.product_code}</label>
                                    <input name="code" value={prodForm.code} onChange={(e) => setProdForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <button type="button" onClick={() => setProdModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf', background: '#fff', fontSize: 13, cursor: 'pointer' }}>{t.cancel_btn}</button>
                                <button type="submit" disabled={actionLoading} style={{
                                    padding: '8px 16px', borderRadius: 8, border: 'none',
                                    background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
                                    cursor: actionLoading ? 'not-allowed' : 'pointer', minWidth: 80,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {actionLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : (prodEditing ? t.save : t.create)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Approved Raw Material Modal */}
            {rawModal && (
                <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
                    <div className="modal-fade-in" style={{ width: 480, maxWidth: '100%', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{rawEditing ? t.edit_raw_material_definition || 'Edit Raw Material Definition' : t.add_raw_material_definition || 'Add Raw Material Definition'}</h2>
                            <button onClick={() => setRawModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
                        </div>
                        <form onSubmit={handleRawSubmit}>
                            <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.material_name}</label>
                                    <input name="name" value={rawForm.name} onChange={(e) => setRawForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.material_code}</label>
                                    <input name="code" value={rawForm.code} onChange={(e) => setRawForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.min_qty_threshold}</label>
                                    <input name="minQuantityThreshold" type="number" value={rawForm.minQuantityThreshold} onChange={(e) => setRawForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <button type="button" onClick={() => setRawModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf', background: '#fff', fontSize: 13, cursor: 'pointer' }}>{t.cancel_btn}</button>
                                <button type="submit" disabled={actionLoading} style={{
                                    padding: '8px 16px', borderRadius: 8, border: 'none',
                                    background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
                                    cursor: actionLoading ? 'not-allowed' : 'pointer', minWidth: 80,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {actionLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : (rawEditing ? t.save : t.create)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Region Modal */}
            {regModal && (
                <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
                    <div className="modal-fade-in" style={{ width: 480, maxWidth: '100%', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{regEditing ? t.edit_region : t.add_region}</h2>
                            <button onClick={() => setRegModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
                        </div>
                        <form onSubmit={handleRegSubmit}>
                            <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.region_name}</label>
                                    <input name="name" value={regForm.name} onChange={(e) => setRegForm(p => ({ ...p, [e.target.name]: e.target.value }))} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <button type="button" onClick={() => setRegModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf', background: '#fff', fontSize: 13, cursor: 'pointer' }}>{t.cancel_btn}</button>
                                <button type="submit" disabled={actionLoading} style={{
                                    padding: '8px 16px', borderRadius: 8, border: 'none',
                                    background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
                                    cursor: actionLoading ? 'not-allowed' : 'pointer', minWidth: 80,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {actionLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : (regEditing ? t.save : t.create)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Floating Menu */}
            {menuOpenId && (() => {
                const list = getCurrentList();
                const item = list.find(i => i._id === menuOpenId);
                if (!item) return null;
                const editFn = getEditHandler();
                const deleteFn = getDeleteHandler();
                return (
                    <div className="floating-menu" style={{
                        position: 'fixed',
                        [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 120),
                        top: menuPos.y, background: '#fff',
                        borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
                        border: '1px solid #ebebeb'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', flexDirection: 'column', padding: 4 }}>
                            <button onClick={() => {
                                setMenuOpenId(null);
                                if (user?.type === 'Member') setShowUnauthorized(true);
                                else editFn(item);
                            }} style={{
                                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                                cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4
                            }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.edit}</button>
                            <button onClick={() => {
                                setMenuOpenId(null);
                                if (user?.type === 'Member') setShowUnauthorized(true);
                                else deleteFn(item);
                            }} style={{
                                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                                cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4
                            }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.delete}</button>
                        </div>
                    </div>
                );
            })()}

            {showUnauthorized && <AccessDeniedModal onBlur={() => setShowUnauthorized(false)} />}
            {deleteConfirm.open && (
                <DeleteConfirmation
                    itemName={deleteConfirm.name}
                    onConfirm={executeDelete}
                    onCancel={() => setDeleteConfirm({ open: false, id: null, type: null, name: '' })}
                    isDeleting={actionLoading}
                />
            )}
        </div>
    );
};

export default Management;
